/* src/Pages/PreOrder/PreOrderUpload/hooks/useFormSubmission.js */
import { useState, useRef, useEffect } from "react";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { showAlert } from "../../../../generalUtils";
import { validateFace, createPreview } from "../../FaceValidation";
import { getFieldValidation } from "../../validationRules/fieldValidationRules";
import { SERVER_URL } from "../../../../config/serverUrl";

const useFormSubmission = (eventName, formConfig) => {
  const { showLoading, hideLoading } = useLoading();
  const [imagePreview, setImagePreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [faceValidation, setFaceValidation] = useState({
    valid: false,
    message: "",
  });
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isModelsLoaded, setIsModelsLoaded] = useState(false);

  // Add state for feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Reference to keep track of the current blob URL
  const currentImageBlobUrl = useRef(null);

  const fileInputRef = useRef(null);
  const serverUrl = SERVER_URL;

  // Check if face-api models are loaded
  if (window.faceapi?.nets?.tinyFaceDetector?.isLoaded && !isModelsLoaded) {
    setIsModelsLoaded(true);
  }

  // Safely revoke object URLs to prevent memory leaks
  const safelyRevokeObjectURL = (url) => {
    if (url && typeof url === "string" && url.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error revoking URL:", error);
      }
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      safelyRevokeObjectURL(currentImageBlobUrl.current);
    };
  }, []);

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Find the matching field in config
    const field = formConfig?.fields?.find((f) => f.id === name);
    if (!field) {
      // If field not found, just update without validation
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // Get validation rule
    const validation = getFieldValidation(field.type);
    let error = validation.validate(value);

    if (field.required && field.type === "select" && field.options) {
      const selectedOption = field.options.find((opt) => opt.value === value);
      if (selectedOption && selectedOption.allowSubmit === false) {
        error = "בחירה זו אינה מאפשרת שליחת הטופס";
      }
    }

    // Update errors
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    // Update value
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Process image selected from gallery
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showAlert("err", "התמונה גדולה מדי - מקסימום 10MB", 3000);
      return;
    }

    setIsValidating(true);
    showLoading();

    try {
      // Read and set the image
      const reader = new FileReader();

      reader.onloadend = async () => {
        const dataUrl = reader.result;

        // Revoke previous blob URL if exists
        safelyRevokeObjectURL(currentImageBlobUrl.current);
        currentImageBlobUrl.current = null;

        // First set the original image so user sees something
        setImagePreview(dataUrl);

        // Make sure window.faceapi is available
        if (!window.faceapi) {
          console.error("❌ ספריית face-api.js לא נמצאה!");
          setFaceValidation({
            valid: false,
            message: "שגיאה: ספריית זיהוי פנים לא זמינה",
          });
          showAlert(
            "err",
            "שגיאה בבדיקת התמונה: ספריית זיהוי פנים לא זמינה",
            3000
          );
          setIsValidating(false);
          hideLoading();
          return;
        }

        try {
          // Call validation function
          const validationResult = await validateFace(dataUrl);

          // Update validation state
          setFaceValidation(validationResult);

          if (validationResult.valid) {
            const enhancedPreview = await createPreview(
              dataUrl,
              validationResult
            );
            if (enhancedPreview && enhancedPreview.startsWith("blob:")) {
              currentImageBlobUrl.current = enhancedPreview;
            }
            setImagePreview(enhancedPreview);
            showAlert("success", "התמונה עברה בדיקה בהצלחה!", 2000);
          } else {
            // Show alert with error message
            showAlert("err", validationResult.message, 4000);
          }
        } catch (validationErr) {
          console.error("❌ שגיאה באימות התמונה:", validationErr);
          setFaceValidation({
            valid: false,
            message: "שגיאה באימות התמונה, אנא נסה שוב",
          });
          showAlert("err", "שגיאה באימות התמונה, אנא נסה שוב", 3000);
        }

        setIsValidating(false);
        hideLoading();
      };

      reader.onerror = (readErr) => {
        console.error("❌ שגיאה בקריאת התמונה:", readErr);
        showAlert("err", "שגיאה בטעינת התמונה", 3000);
        setIsValidating(false);
        hideLoading();
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error("❌ שגיאה כללית בעיבוד התמונה:", err);
      showAlert("err", "שגיאה בעיבוד התמונה", 3000);
      setIsValidating(false);
      hideLoading();
    }
  };

  // Open camera modal
  const openCamera = () => {
    setShowCamera(true);
  };

  // Close camera modal
  const closeCamera = () => {
    setShowCamera(false);
  };

  // Handle image captured from camera
  const handleCapturedImage = async (imageDataUrl) => {
    // Revoke previous blob URL
    safelyRevokeObjectURL(currentImageBlobUrl.current);

    // Store the new URL if it's a blob
    if (imageDataUrl && imageDataUrl.startsWith("blob:")) {
      currentImageBlobUrl.current = imageDataUrl;
    } else {
      currentImageBlobUrl.current = null;
    }

    setImagePreview(imageDataUrl);
    // Images from the camera component are already validated
    setFaceValidation({
      valid: true,
      message: "תמונה נלקחה בהצלחה! אפשר להמשיך",
    });
    closeCamera();
  };

  // Remove selected image
  const removeImage = () => {
    // Cleanup the current blob URL
    safelyRevokeObjectURL(currentImageBlobUrl.current);
    currentImageBlobUrl.current = null;

    setImagePreview(null);
    setFaceValidation({ valid: false, message: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Function to close the feedback modal and reset the form
  const closeFeedbackModal = () => {
    // First close the modal
    setShowFeedbackModal(false);

    // Then reset the form
    setFormData({});
    setFormErrors({});
    setImagePreview(null);
    setFaceValidation({ valid: false, message: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle form submission with improved validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if formConfig exists
    if (!formConfig) {
      showAlert("err", "טופס לא נטען עדיין", 3000);
      return;
    }

    // Full validation of all fields before submission
    let hasErrors = false;
    const newErrors = {};
    let firstErrorField = null;
    formConfig.fields?.forEach((field) => {
      const value = formData[field.id];

      // Check if field is required
      if (
        field.required &&
        (!value || (typeof value === "string" && value.trim() === ""))
      ) {
        newErrors[field.id] = "שדה זה נדרש למילוי";
        hasErrors = true;
        if (!firstErrorField) firstErrorField = field.id;
        return;
      }

      // Check select fields for allowed options
      if (field.required && field.type === "select" && value) {
        const selectedOption = field.options?.find(
          (opt) => opt.value === value
        );
        if (selectedOption && selectedOption.allowSubmit === false) {
          newErrors[field.id] = "בחירה זו אינה מאפשרת שליחת הטופס";
          hasErrors = true;
          if (!firstErrorField) firstErrorField = field.id;
          return;
        }
      }

      // Check "Other" option text field validation (single-select)
      if (field.type === "select" && !field.isMultiSelect && value) {
        const selectedOption = field.options?.find(
          (opt) => opt.value === value
        );
        if (selectedOption?.isOther && selectedOption?.showTextField !== false && selectedOption?.textFieldRequired) {
          const otherTextKey = `${field.id}_otherText`;
          const otherText = formData[otherTextKey];
          if (!otherText || (typeof otherText === "string" && otherText.trim() === "")) {
            newErrors[otherTextKey] = "נא לפרט";
            hasErrors = true;
            if (!firstErrorField) firstErrorField = otherTextKey;
          }
        }
      }

      // Check "Other" option text field validation (multi-select)
      if (field.type === "select" && field.isMultiSelect && Array.isArray(value)) {
        if (value.includes("other")) {
          const otherOption = field.options?.find((opt) => opt.isOther);
          if (otherOption && otherOption.showTextField !== false && otherOption.textFieldRequired) {
            const otherTextKey = `${field.id}_otherText`;
            const otherText = formData[otherTextKey];
            if (!otherText || (typeof otherText === "string" && otherText.trim() === "")) {
              newErrors[otherTextKey] = "נא לפרט";
              hasErrors = true;
              if (!firstErrorField) firstErrorField = otherTextKey;
            }
          }
        }
      }

      // Skip validation for file fields - they are validated in UserForm
      if (field.type === "file") {
        return;
      }

      // Check validation by type for non-file fields
      if (value && typeof value === "string") {
        const validation = getFieldValidation(field.type);
        const error = validation.validate(value);

        if (error) {
          newErrors[field.id] = error;
          hasErrors = true;
          if (!firstErrorField) firstErrorField = field.id;
        }
      }
    });

    if (hasErrors) {
      setFormErrors(newErrors);
      showAlert("err", "יש לתקן את השגיאות בטופס", 3000);

      // Scroll to first error field
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      return;
    }

    // Check if image is required based on config
    const imageRequired = formConfig.imageRequired !== false; // Default to true if not specified
    const showImageSection = formConfig.showImageSection !== false; // Default to true if not specified

    // Check if image exists and is valid when required
    if (showImageSection && imageRequired && !imagePreview) {
      showAlert("err", "יש לבחור או לצלם תמונה", 3000);
      return;
    }

    if (
      showImageSection &&
      imageRequired &&
      !faceValidation.valid &&
      imagePreview
    ) {
      showAlert("err", "התמונה אינה תקינה: " + faceValidation.message, 3000);
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting) return;
    setIsSubmitting(true);
    showLoading();

    try {
      const formPayload = new FormData();
      formPayload.append("eventName", eventName);
      if (formConfig && formConfig.fields) {
        formConfig.fields.forEach((field) => {
          const fieldValue = formData[field.id];
          if (field.type === "file") {
            if (fieldValue) {
              formPayload.append(
                field.id,
                fieldValue,
                fieldValue.name || "file"
              );
            }
            return;
          }

          if (field.type === "checkbox") {
            formPayload.append(field.label, fieldValue ? "true" : "false");
            return;
          }

          if (field.type === "select") {
            // Handle multi-select (array of values)
            if (field.isMultiSelect && Array.isArray(fieldValue)) {
              const selectedLabels = fieldValue
                .map((val) => {
                  const opt = field.options?.find((o) => o.value === val);
                  if (!opt) return null;
                  if (opt.isOther) {
                    const otherTextKey = `${field.id}_otherText`;
                    const otherText = formData[otherTextKey]?.trim();
                    return otherText ? `${opt.label.trim()} - ${otherText}` : opt.label.trim();
                  }
                  return opt.label;
                })
                .filter(Boolean)
                .join(", ");
              formPayload.append(field.label, selectedLabels || "");
              return;
            }

            // Handle regular single-select
            const selected = field.options?.find(
              (opt) => opt.value === fieldValue
            );
            if (selected) {
              // Handle "Other" option with text field
              if (selected.isOther) {
                const otherTextKey = `${field.id}_otherText`;
                const otherText = formData[otherTextKey]?.trim();
                if (otherText) {
                  // Format: "אחר - <text>"
                  formPayload.append(field.label, `${selected.label.trim()} - ${otherText}`);
                } else {
                  // Just the label if no text
                  formPayload.append(field.label, selected.label.trim());
                }
              } else {
                formPayload.append(field.label, selected.label.trim());
              }
            }
            return;
          }

          if (typeof fieldValue === "string" && fieldValue.trim() !== "") {
            formPayload.append(field.label, fieldValue.trim());
          }
        });
      }

      // Only add image if it exists
      if (imagePreview) {
        try {
          // Convert image data URL to blob for upload
          const response = await fetch(imagePreview);
          const blob = await response.blob();
          formPayload.append("image", blob, "image.jpg");
        } catch (imageError) {
          console.error("Error processing image:", imageError);
          hideLoading();
          showAlert("err", "שגיאה בעיבוד התמונה, אנא נסה שוב", 3000);
          setIsSubmitting(false);
          return;
        }
      }

      // Send data to server
      let serverResponse;
      try {
        serverResponse = await fetch(`${serverUrl}/api/upload-preorder-image`, {
          method: "POST",
          body: formPayload,
        });
      } catch (networkError) {
        console.error("Network error during submission:", networkError);
        hideLoading();
        showAlert("err", "שגיאת תקשורת בשליחת הטופס, אנא נסה שוב", 5000);
        setIsSubmitting(false);
        return;
      }

      // Handle server response
      if (serverResponse.ok) {
        try {
          await serverResponse.json();

          // Show success alert - using "warn" since there's no "success" in your showAlert
          showAlert(
            "success",
            showImageSection && imagePreview
              ? "הטופס והתמונה נשלחו בהצלחה! תודה על שיתוף הפעולה."
              : "הטופס נשלח בהצלחה! תודה על שיתוף הפעולה.",
            5000
          );

          // Always show confirmation window after successful submission, even without image
          setShowFeedbackModal(true);
        } catch (parseError) {
          console.error("Error parsing server response:", parseError);
          // Even if parsing fails, consider the submission successful
          showAlert("success", "הטופס נשלח בהצלחה!", 5000);
          setShowFeedbackModal(true);
        }
      } else {
        // Error case - first show a general error message
        showAlert(
          "err",
          `שגיאה בשליחת הטופס (קוד ${serverResponse.status})`,
          5000
        );

        try {
          // Then try to get more specific error details
          const errorText = await serverResponse.text();

          try {
            // Try to parse as JSON
            const errorData = JSON.parse(errorText);
            if (errorData && errorData.error) {
              // Show specific error message
              showAlert("err", errorData.error, 5000);
            }
          } catch (jsonError) {
            console.error("Error response is not valid JSON:", jsonError);
            // If not JSON, show raw error text if it's not too long
            if (errorText && errorText.trim() && errorText.length < 100) {
              showAlert("err", "שגיאה: " + errorText, 5000);
            }
          }
        } catch (parseError) {
          console.error("Error parsing error response:", parseError);
        }
      }
    } catch (error) {
      console.error("Error during form submission:", error);
      showAlert(
        "err",
        "שגיאה בשליחת הטופס: " + (error.message || "אנא נסה שוב"),
        5000
      );
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    imagePreview,
    faceValidation,
    showCamera,
    isSubmitting,
    isValidating,
    fileInputRef,
    formErrors,
    handleInputChange,
    handleFileChange,
    openCamera,
    closeCamera,
    handleCapturedImage,
    removeImage,
    handleSubmit,
    // Add new feedback modal states and functions
    showFeedbackModal,
    closeFeedbackModal,
  };
};

export default useFormSubmission;
